import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Droplet, Clock } from "lucide-react";
import { patientDol, humanDurationBetween, fmtLocalDT } from "@/lib/clinical";

// Simple neonatal fluid calc — total ml/kg/day per Day of Life for term.
const DEFAULT_TOTAL_BY_DOL = { 1: 60, 2: 80, 3: 100, 4: 120, 5: 140, 6: 150, 7: 150 };

function fmtDT(iso) { return fmtLocalDT(iso); }

export default function FluidTab({ patient }) {
  const derivedDol = patientDol(patient) ?? 1;
  const [weightKg, setWeightKg] = useState(((patient.current_weight_g || patient.birth_weight_g || 0) / 1000).toFixed(2));
  const [dol, setDol] = useState(derivedDol);
  const [totalPerKg, setTotalPerKg] = useState(DEFAULT_TOTAL_BY_DOL[Math.min(Number(derivedDol), 7)] || 150);
  const [feedMl, setFeedMl] = useState(0);
  const [otherIvMl, setOtherIvMl] = useState(0);
  const [dextrose, setDextrose] = useState(10);
  const [plans, setPlans] = useState([]);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/fluids/${patient.id}`);
      setPlans(data || []);
    } catch (e) { console.warn("Fluid history load failed", e); }
  }, [patient.id]);

  useEffect(() => { load(); }, [load]);

  // Update the "running since" duration once a minute without a full refetch.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const current = plans[0] || null;

  const totals = useMemo(() => {
    const w = Number(weightKg) || 0;
    const total = Number(totalPerKg) * w;
    const remaining = Math.max(total - Number(feedMl) - Number(otherIvMl), 0);
    const hourly = remaining / 24;
    const dextRate = (Number(dextrose) / 100) * (hourly * 1000) / (w * 60);
    return { total, remaining, hourly, gir: isFinite(dextRate) ? dextRate : 0 };
  }, [weightKg, totalPerKg, feedMl, otherIvMl, dextrose]);

  const save = async () => {
    try {
      await api.post("/fluids", {
        patient_id: patient.id,
        weight_kg: Number(weightKg), day_of_life: Number(dol),
        gestational_age_weeks: patient.gestational_age_weeks,
        total_ml_per_kg_per_day: Number(totalPerKg),
        feed_ml_per_day: Number(feedMl), other_iv_ml_per_day: Number(otherIvMl),
        gir: Number(totals.gir.toFixed(2)),
      });
      toast.success("Fluid plan saved");
      load();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-6" data-testid="fluid-tab">
      {/* Current running plan */}
      {current && (
        <div className="card-clinical p-5 border-l-4 border-l-primary/80" data-testid="fluid-current-plan">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl grid place-items-center bg-primary/10 text-primary">
                <Droplet size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Current fluid plan</div>
                <div className="font-medium">
                  {current.total_ml_per_kg_per_day} ml/kg/day
                  <span className="text-muted-foreground"> · IV</span>{" "}
                  <span className="tabular">{(current.hourly_rate_ml || 0).toFixed(1)}</span>
                  <span className="text-muted-foreground"> ml/h</span>
                  {current.gir != null && (
                    <>
                      <span className="text-muted-foreground"> · GIR </span>
                      <span className="tabular">{Number(current.gir).toFixed(2)}</span>
                      <span className="text-muted-foreground"> mg/kg/min</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 justify-end">
                <Clock size={11} /> Running since
              </div>
              <div className="text-sm font-medium tabular">
                <span data-testid="fluid-duration" data-tick={tick}>{humanDurationBetween(current.at)}</span>
                <span className="text-muted-foreground text-xs"> · started {fmtDT(current.at)}</span>
              </div>
              {current.by && <div className="text-[11px] text-muted-foreground">by {current.by}</div>}
            </div>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Total {Math.round((current.total_ml_per_kg_per_day || 0) * (current.weight_kg || 0))} ml/day
            {" · "}
            Feeds {current.feed_ml_per_day || 0} ml/day
            {" · "}
            Other IV {current.other_iv_ml_per_day || 0} ml/day
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
        <div className="card-clinical p-6 space-y-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Inputs</div>
          <Row label="Weight (kg)"><Input type="number" step="0.01" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} data-testid="fluid-weight" /></Row>
          <Row label={`Day of life${patient.dob ? " (auto)" : ""}`}>
            <Input
              type="number" value={dol}
              onChange={(e) => { setDol(e.target.value); setTotalPerKg(DEFAULT_TOTAL_BY_DOL[Math.min(Number(e.target.value) || 1, 7)] || 150); }}
              data-testid="fluid-dol"
            />
          </Row>
          <Row label="Total (ml/kg/day)"><Input type="number" value={totalPerKg} onChange={(e) => setTotalPerKg(e.target.value)} /></Row>
          <Row label="Feed (ml/day)"><Input type="number" value={feedMl} onChange={(e) => setFeedMl(e.target.value)} /></Row>
          <Row label="Other IV (ml/day)"><Input type="number" value={otherIvMl} onChange={(e) => setOtherIvMl(e.target.value)} /></Row>
          <Row label="Dextrose (%)"><Input type="number" value={dextrose} onChange={(e) => setDextrose(e.target.value)} /></Row>
          <div className="flex justify-end"><Button onClick={save} className="rounded-full" data-testid="fluid-save">Save plan</Button></div>
        </div>

        <div className="card-clinical p-6 space-y-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Calculated (new plan)</div>
          <BigStat label="Total fluid" value={`${totals.total.toFixed(0)} ml/day`} />
          <BigStat label="Remaining IV" value={`${totals.remaining.toFixed(0)} ml/day`} />
          <BigStat label="IV hourly rate" value={`${totals.hourly.toFixed(1)} ml/h`} />
          <BigStat label="GIR (approx.)" value={`${totals.gir.toFixed(2)} mg/kg/min`} />
          <div className="text-xs text-muted-foreground">
            Guideline defaults: DOL 1 = 60, 2 = 80, 3 = 100, 4 = 120, 5+ = 140–150 ml/kg/day (term). Adjust for preterm & losses.
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-center gap-3">
      <label className="text-xs text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
function BigStat({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-display font-semibold tabular text-3xl mt-1">{value}</div>
    </div>
  );
}
